<script setup lang="ts">
/**
 * /audit-logs —— 审计日志与告警流(全新页,v2 · O3;S-44)。
 *
 * ═══ 这一页为什么存在(规格 §6 第一层)═══
 *
 * 审计流水自 S-10 起就在契约里,而**运营端从来没有一页能看它**:几条决策的后半句
 * 都是「落了日志」—— 联系人解锁(M-08)、优先窗口独占(L0-05)、回调幂等命中(M-15)
 * —— 而在此之前要核这句话只能进库翻表。
 *
 * 最要紧的是手机号那五条(M-17):额度耗尽的表现是**全站登录静默失败**
 * (用户点了没反应,没有 5xx、没有堆栈、服务端一个请求都收不到),这几条日志是
 * 唯一能让它被看见的东西。而它们**全都没有单号** —— 订单驾驶舱的抽屉是按单查的,
 * 永远查不到它们。顶部那块告警流就是为这五条存在的。
 *
 * ═══ 这一页只读 ═══
 *
 * 只调契约里的那一条 GET(listAuditLogs),没有任何写请求:审计表只增不删(AS-006),
 * 运营端在这张表上不制造任何状态。点一行、展开明细都只是把它读出来。
 *
 * ═══ 告警流:从真实审计数据派生,不是第二份数据 ═══
 *
 * 画板给的是三行示例(手机号接口额度告警 / 对象存储签名失败 / 支付回调重复推送命中),
 * 而**没有任何端点返回「告警」**:既没有待处理队列,也没有「哪些事件算告警」的登记表。
 * 这一页的处置是:告警集 = 手机号接口的五类哨兵事件(S-44 把那份名单落进
 * packages/shared 的 PHONE_ALERT_EVENT_TYPES,与枚举同源),每类各查一次
 * (eventType 是契约真有的过滤参数),合并按时刻倒序取最近几条 —— 全是真数据,
 * 没有本地假数据、没有模板占位。
 *
 * 三件事刻意**不做**:
 *   · 「3 条未处理」/「已处理」不画:审计日志只增不删,库里没有任何处理态字段
 *     承载它 —— 画一个计数就是一句没人能兑现的承诺(画板那一句登记在 design-copy 的
 *     _notRequired 里)。这一页只答「有没有发生」,不答「有没有人管了」。
 *   · 五类之外的告警(画板还画了存储签名失败与支付回调重复推送)不进这一块 ——
 *     「哪些算告警」没有登记表,而这一页的理由是电话类那一条静默失败链路;
 *     要不要扩、要不要服务端给一条「待处理告警」端点,记在 assumptions 的 AS-091。
 *   · 告警行的标题/说明**不是**画板写的那三句人话:「事件类型 → 人话」的词表全仓
 *     没有(枚举里只有英文码),服务端也不下发 —— 行里渲染的是事件码 + detail 原文。
 *
 * 取数失败的口径:五类里**任意一类取失败,整块告警区就不显示任何告警行**,
 * 只显示那句失败原因。把「取到的四条」画成「今天的全部告警」正是这一页最不该犯的错
 * —— 它存在的理由就是别漏看(M-17 的代价是「发现路径只剩人工」)。
 *
 * ═══ 与画板的已知差异(都记在 design-copy 的 O3 帧里)═══
 *
 * ① 事件类型渲染**英文原码**:AS-035 已裁,审计明细是取证视图(出纠纷时拿页面上的字
 *    去核对日志、代码与库里那一列),译成中文会多一次人工反查。画板的六个样例外
 *    英文码与真实枚举零交集,是示意数据,不实现(登记进 _notRequired)。
 * ② 对象列 = orderNo 或破折号(非订单事件没有单号,画板的「平台配置」「上传凭证」
 *    那类文本无处可来);操作人列 = actorId;来源列没有字段 → 破折号。
 *    后两处各登记一条 @unimplemented 标记(见下面那两条)。
 * ③ 详情列是原样 JSON,长的那种收在「明细」开关后(照 O1 审计卡的做法)。
 * ④ 过滤控件(事件类型 / 单号)与分页控件是**画板没画的补件**:两个参数契约里都有,
 *    而「找某一单的流水」正是这一页的价值;列表是分页的,没有页码控件 50 条之后的
 *    流水会静默看不见。
 */
import { computed, h, onMounted, ref, type VNode } from 'vue'
import { NInput, NSelect, useMessage, type DataTableColumns } from 'naive-ui'
import {
  AUDIT_EVENT_TYPE,
  PHONE_ALERT_EVENT_TYPES,
  formatShanghaiMinute,
  type AuditEventType,
} from '@sea-drone/shared'
import {
  DataTable,
  EmptyState,
  ErrorBanner,
  NavIcon,
  PageHeader,
  PanelCard,
  ScopeNotes,
  type ScopeNote,
} from '@sea-drone/ui'
import { api, ApiError, type AuditLogItem } from '../api/client'

/** 这一页是运营端的第三块深色页,底座由 S-41 的第三档 tone 给(AppShell 上已挂同一档) */
const TONE = 'ops-dark' as const

const PAGE_SIZE = 50

/**
 * 告警流每类取几条、合并后展示几条。取 5 是因为「最近几条」足够回答「现在在出什么事」,
 * 而每类 5 条 × 五类最多 25 条、合并后留 5 条 —— 全量条数是五个 total 之和(见下),
 * 所以这里截断不会让「有没有告警」这个问题失去答案。
 */
const ALERT_PER_TYPE = 5

const message = useMessage()

const rows = ref<AuditLogItem[]>([])
/** 返回体里的 total —— 当前过滤条件下的**全量**条数(服务端计数,不是本页行数) */
const total = ref(0)
const page = ref(1)
const loading = ref(false)
const lastError = ref<string | null>(null)

/** 契约真有的两个过滤参数(画板没画控件,这是补件) */
const eventTypeFilter = ref<AuditEventType | null>(null)
const orderNoFilter = ref('')

/**
 * 告警流。`alerts === null` 表示「还没取到或取失败」—— 与「取到了、一条都没有」
 * 是两回事,页面上也必须长得不一样(空态说「没有告警」,失败说「没取到」)。
 */
const alerts = ref<AuditLogItem[] | null>(null)
/** 五类哨兵事件的**全量**条数:五个 eventType 各自的 total 之和(五类互不相交) */
const alertTotal = ref<number | null>(null)
const alertError = ref<string | null>(null)

/** 展开着明细的那一行(点一下展开、再点收起;键见 rowKeyOf) */
const openDetail = ref<string | null>(null)

/** 事件类型下拉:取值与文案都取 shared 的枚举 —— 英文原码(AS-035),不写第二份中文名 */
const eventTypeOptions = AUDIT_EVENT_TYPE.map((t) => ({ label: t, value: t }))

const NOTES: readonly ScopeNote[] = [
  {
    title: '这一页能做什么',
    body:
      '看全部审计流水 —— 每一行是一条真的落过库的事件:时间 / 事件类型 / 对象 / 操作人 / 详情。' +
      '可按事件类型与单号筛。联系人解锁、优先窗口独占、回调幂等命中这三条决策的后半句都是' +
      '「落了日志」,而在此之前核这句话只能进库翻表;手机号接口那五条更是连单号都没有,' +
      '订单驾驶舱按单查永远查不到它们。',
    refs: ['S-10', 'M-08', 'L0-05', 'M-15'],
  },
  {
    title: '告警流这五类是怎么挑出来的',
    body:
      '告警区不是第二份数据:它就是审计表里**手机号接口的五类哨兵事件' +
      '(接口不可用 / 额度耗尽 / 未知错误 / 超时 / code 已被用过)**的派生视图,每类各查一次真接口,' +
      '合并按时刻倒序。挑这五类的理由:这五种失败在页面上长得一模一样(都是「登录不动了」),' +
      '而处置动作各不相同(充值 / 修服务 / 读文档 / 核一笔可能已扣的费 / 让用户重点一次)。' +
      '额度耗尽的表现是全站登录静默失败 —— 服务端一个请求都收不到,这几条日志是唯一的发现路径。' +
      '画板上那三类里还有「对象存储签名失败」与「支付回调重复推送」,这一版没纳进来(见下面那条)。',
    refs: ['M-17', 'S-44'],
  },
  {
    title: '为什么没有「未处理 / 已处理」',
    body:
      '画板右上角写着「3 条未处理」,而**审计日志只增不删,库里没有任何处理态字段**承载它:' +
      '没有「谁看过」「谁处置了」,也没有任何地方能把这几个字兑现。所以这一块只答' +
      '「有没有发生」,不答「有没有人管了」—— 一条兑现不了的承诺比不写更糟。' +
      '右上角那个数是「这五类事件的总条数」,不是待办数。',
    refs: ['S-44'],
  },
  {
    title: '事件类型为什么是英文原码',
    body:
      '审计明细是**取证视图**:出纠纷时人要拿页面上的字去核对日志、服务端代码与库里那一列,' +
      '而事件名在那三处都是英文常量。译成中文会多出一次人工反查,而一个翻错的词看起来' +
      '同样是正常的中文 —— 这类错不会报错。这一条 2026-09-22 已裁(AS-035,两个端唯一' +
      '保留机器名的地方)。',
    refs: ['AS-035'],
  },
  {
    title: '来源列与操作人那一列',
    body:
      '「来源」在契约的审计条目里**没有对应字段**(只有事件类型 / 单号 / 操作人 / 明细 / 时刻),' +
      '所以那一列画破折号。「操作人」渲染的是 actorId(运营账号的 id),**不是人名** ——' +
      '契约里没有账号名单接口,人名无处可来,不拿别处的名字顶替(那是一句看不出来的假话)。' +
      '两处各登记了一条 @unimplemented 标记。',
    refs: ['S-44'],
  },
  {
    title: '画板上的三条告警名称与说明',
    body:
      '画板画的是「手机号接口额度告警」「剩余 62 次,按当前用量约 2 天耗尽」这样的标题与说明,' +
      '而「事件类型 → 人话标题」的词表**全仓没有**(枚举里只有英文码),服务端也不下发它们 ——' +
      '硬凑一份就是二十一条没人能验的中文。所以告警行渲染事件码 + detail 原文,' +
      '画板那三句登记在 design-copy 的 _notRequired 里。',
    refs: ['S-44', 'AS-091'],
  },
  {
    title: '过滤与翻页',
    body:
      '事件类型与单号是契约真有的两个参数(画板没画控件 —— 这是补件:「找某一单的流水」' +
      '正是这一页的价值所在)。列表按契约分页(每页 50 条),页码在表格右下角;' +
      '「共 N 条」是服务端在当前条件下数的,不是本页行数。',
    refs: ['S-44'],
  },
]

/** 挂在告警卡标题旁边的那几条 —— 它比页头那一条更该贴着这块区域说 */
const ALERT_NOTES: readonly ScopeNote[] = [
  {
    title: '这块区域的口径',
    body:
      '从审计表里派生:手机号接口的五类哨兵事件(额度耗尽 / 接口不可用 / 未知错误 / 超时 /' +
      'code 已被用过),每类各查一次真接口,合并按时刻倒序取最近几条。右上角那个数是' +
      '**五类的全量条数**(五个 eventType 各自的 total 之和),不是待办数 —— 「未处理 / 已处理」' +
      '在库里没有承载,这一页不承诺。',
    refs: ['M-17', 'S-44'],
  },
  {
    title: '取数失败时这里为什么不显示告警行',
    body:
      '五类里任意一类取失败,整块区域就只显示失败原因、不显示任何告警行:把「取到的四条」' +
      '画成「今天的全部告警」,正是这一页最不该犯的错 —— 这一块存在的理由就是别漏看。',
    refs: ['S-44'],
  },
]

// ── 两条缺口(画「—」或替代物的理由各不同)────────────────────
//
// @unimplemented(reason="「来源」列在契约的审计条目里没有对应字段(AuditLogItem 只有 eventType / orderNo / actorId / detail / occurredAt),画板那五个值(飞手端 / 运营端 / 微信回调 / 定时任务 / 服务端)无处可来 —— 从事件类型猜一个来源是编数据,而它看起来完全正常。按「拿不到就画破折号」处理。要它先给 AuditLogItem 加一个来源字段(或者一条事件类型→来源的登记表)", owner="me", blocks="O3 表格「来源」列")
//
// @unimplemented(reason="「操作人」那一列画板画的是人名(王建国 / 运营 李工 / 系统),而契约只有 actorId(运营账号的 id)。账号名单没有接口:盘点的只有监管端账号(listGovAccounts),运营账号列表不存在;拿订单或承运商里的名字逐行顶替是一句看不出来的假话。按「有什么渲染什么」处理:画 actorId。要人名先给一条运营账号的查询入口", owner="me", blocks="O3 表格「操作人」列的人名")

/**
 * 六列,逐列一的数据源(画板 O3 的表头逐字):
 *   时间 occurredAt(东八区、到分钟,照 O1 的形态)· 事件类型 eventType(**英文原码**,AS-035)
 *   · 对象 orderNo 或破折号(非订单事件没有单号)· 操作人 actorId 或破折号
 *   · 来源 **无字段** → 破折号 · 详情 detail 原样 JSON(长的那种收在开关后)。
 */
const columns = computed<DataTableColumns<AuditLogItem>>(() => [
  { title: '时间', key: 'occurredAt', width: 110, render: (row) => shortTime(row.occurredAt) },
  // 事件名渲染**英文原码**:AS-035 已裁(取证视图,译成中文会多一次人工反查)
  {
    title: '事件类型',
    key: 'eventType',
    width: 230,
    render: (row) => h('span', { class: 'mono' }, row.eventType),
  },
  {
    title: '对象',
    key: 'orderNo',
    width: 170,
    render: (row) =>
      row.orderNo === null
        ? h('span', { class: 'dash' }, '—')
        : h('span', { class: 'mono' }, row.orderNo),
  },
  {
    title: '操作人',
    key: 'actorId',
    width: 170,
    render: (row) =>
      row.actorId === null
        ? h('span', { class: 'dash' }, '—')
        : h('span', { class: 'mono' }, row.actorId),
  },
  // 来源:契约里没有这个字段 —— 破折号,理由见上面那条 @unimplemented
  { title: '来源', key: 'source', width: 100, render: () => h('span', { class: 'dash' }, '—') },
  { title: '详情', key: 'detail', render: (row) => detailCell(row) },
])

/**
 * 行键:契约的审计条目**没有 id**(只有事件类型 / 单号 / 操作人 / 明细 / 时刻),
 * 所以键要从内容拼出来 —— 连明细一起拼,免得同一毫秒的同一类事件撞成一个键
 * (行键重复的表现是「展开这一行的明细,另一行跟着动」,页面上看不出哪里错了)。
 */
function rowKeyOf(row: AuditLogItem): string {
  return `${row.occurredAt}|${row.eventType}|${row.orderNo ?? ''}|${row.actorId ?? ''}|${JSON.stringify(row.detail ?? {})}`
}

/**
 * 时间的形态 `10-15 14:22`:共享包给的是 `2026-10-15 14:22`(东八区,Y-03),
 * 把年份切掉就是画板那一格。手写 toLocaleString 会在容器 TZ=UTC 时显示早 8 小时的时刻。
 */
function shortTime(iso: string): string {
  return formatShanghaiMinute(iso).slice(5)
}

/** 明细有没有东西:空对象展开是一片 `{}`,那一格该画破折号 */
function hasDetail(row: AuditLogItem): boolean {
  return Object.keys(row.detail ?? {}).length > 0
}

/**
 * 明细的一行预览:原样 JSON。**不给它写「人话」改写** —— 与事件名保留英文同一条口径
 * (AS-035):这一页上的字是用来跟日志、代码与库核对的,改写一层就多一次反查。
 */
function detailText(row: AuditLogItem): string {
  return JSON.stringify(row.detail ?? {})
}

/** 详情格:有键才给开关(空对象展开是一片 `{}`);开关的做法照 O1 的审计卡 */
function detailCell(row: AuditLogItem): VNode {
  if (!hasDetail(row)) return h('span', { class: 'dash' }, '—')
  const key = rowKeyOf(row)
  const open = openDetail.value === key
  return h('div', { class: 'detail-cell' }, [
    h('span', { class: 'detail-json' }, detailText(row)),
    h(
      'button',
      {
        type: 'button',
        class: 'detail-toggle',
        onClick: () => {
          openDetail.value = open ? null : key
        },
      },
      open ? '收起' : '明细',
    ),
    open ? h('pre', { class: 'detail-pre' }, JSON.stringify(row.detail, null, 2)) : null,
  ])
}

/** 错误信息:照 SettlementsView 的口径 —— 服务端原文 + requestId,一个都不丢 */
function messageOf(e: unknown): string {
  return e instanceof ApiError
    ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
    : e instanceof Error
      ? e.message
      : String(e)
}

function show(e: unknown): void {
  const msg = messageOf(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

/** 列表取数(当前条件下的一页);换页或换条件时展开着的明细一并收起 */
async function refreshTable(): Promise<void> {
  openDetail.value = null
  loading.value = true
  try {
    const res = await api.listAuditLogs({
      page: page.value,
      pageSize: PAGE_SIZE,
      ...(eventTypeFilter.value ? { eventType: eventTypeFilter.value } : {}),
      ...(orderNoFilter.value.trim() ? { orderNo: orderNoFilter.value.trim() } : {}),
    })
    rows.value = res.items
    total.value = res.total
    lastError.value = null
  } catch (e) {
    show(e)
  } finally {
    loading.value = false
  }
}

/**
 * 告警流取数:五类哨兵事件各查一次(契约的 eventType 是单值参数),合并按时刻倒序。
 *
 * 失败口径见文件头:任意一类失败 → 整块不显示告警行。这里不 throw(调用方是刷新按钮
 * 与 onMounted,它们没有别的处理动作),但失败被写进 alertError 并显示在区域里 ——
 * 不许静默吞掉。
 */
async function refreshAlerts(): Promise<void> {
  alertError.value = null
  try {
    const pages = await Promise.all(
      PHONE_ALERT_EVENT_TYPES.map((eventType) =>
        api.listAuditLogs({ eventType, pageSize: ALERT_PER_TYPE }),
      ),
    )
    alerts.value = pages
      .flatMap((p) => p.items)
      .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
      .slice(0, ALERT_PER_TYPE)
    alertTotal.value = pages.reduce((sum, p) => sum + p.total, 0)
  } catch (e) {
    alerts.value = null
    alertTotal.value = null
    alertError.value = messageOf(e)
  }
}

/** 刷新:列表与告警流一起重取(页头那颗刷新键按的就是它) */
async function refreshAll(): Promise<void> {
  await Promise.all([refreshTable(), refreshAlerts()])
}

/** 两个过滤控件都走这里:条件变了就回第一页,否则会停在一个空的高页码上 */
function applyFilters(): void {
  page.value = 1
  void refreshTable()
}

/** 翻页:服务端分页(契约的 page/pageSize),不在本地切片 —— total 是服务端的数 */
function goPage(next: number): void {
  page.value = next
  void refreshTable()
}

/**
 * 告警圆点的严重度:**去充值那一档最急**(额度耗尽时全站登录已经开始静默失败),
 * 其余四类是需要人去查(修服务 / 读文档 / 核一笔可能已扣的费 / 让用户重点一次)。
 * 颜色走令牌的语义色(深色档),不写画板取样出来的那两个裸琥珀。
 */
function dotClass(eventType: AuditEventType): string {
  return eventType === 'phone_quota_exhausted' ? 'dot-danger' : 'dot-warning'
}

/** 右上角那个数。展示行数少于全量时把「最近 N 条 · 共 M 条」都说出来,不含糊 */
const alertCountText = computed(() => {
  const tot = alertTotal.value
  if (tot === null) return ''
  const shown = alerts.value?.length ?? 0
  return tot > shown ? `最近 ${shown} 条 · 共 ${tot} 条` : `共 ${tot} 条`
})

onMounted(() => {
  void refreshAll()
})
</script>

<template>
  <div class="page">
    <PageHeader :tone="TONE" title="审计日志" :notes="NOTES">
      <template #actions>
        <!-- 画板页头右端那一行:先说明(逐字)、再刷新(幽灵键,照 O2 那一颗) -->
        <span class="header-note">不带单号的事件流,原先三端都看不见</span>
        <button type="button" class="ghost" :disabled="loading" @click="refreshAll">
          <NavIcon name="refresh" :size="14" />
          刷新
        </button>
      </template>
    </PageHeader>

    <ErrorBanner v-if="lastError" :tone="TONE" :message="lastError" title="上一次操作失败" />

    <!--
      告警流(画板那一块,1128×283)。底与描边走语义 warning 的两档透明度
      (深色档 = rgba(245,177,65,.12 / .25))—— 画板取样的是 #D98A00 那个琥珀,
      它在令牌表里没有位置,最近的语义色就是 warning 这一族,按「不新增令牌、不写裸色值」处理。
      右上角是**全量条数**(五个 total 之和),不是画板的「3 条未处理」——「未处理」没有承载。
    -->
    <section class="alerts">
      <header class="alerts-head">
        <div class="alerts-title-row">
          <span class="alerts-title">需要有人看的告警</span>
          <ScopeNotes :notes="ALERT_NOTES" :tone="TONE" />
        </div>
        <span v-if="alertCountText" class="alerts-count">{{ alertCountText }}</span>
      </header>

      <!-- 取失败:整块不显示告警行(见文件头那一段口径),只把这句原因亮出来 -->
      <p v-if="alertError" class="alerts-error">
        告警流取数失败:{{ alertError }} —— 五类里有一类没取到,这里就不显示任何告警行,
        不把取到的那几条画成今天的全部告警。
      </p>
      <p v-else-if="alerts === null" class="alerts-loading">正在取告警流…</p>
      <EmptyState
        v-else-if="alerts.length === 0"
        :tone="TONE"
        text="五类电话告警一条都没有"
        hint="这一块只在手机号接口出问题时出现东西 —— 额度耗尽 / 接口不可用时,它会先于客户反馈亮起来"
      />
      <ul v-else class="alert-list">
        <li v-for="a in alerts" :key="rowKeyOf(a)" class="alert-row">
          <span class="alert-dot" :class="dotClass(a.eventType)" aria-hidden="true" />
          <span class="alert-time">{{ shortTime(a.occurredAt) }}</span>
          <span class="alert-main">
            <span class="alert-event">{{ a.eventType }}</span>
            <span class="alert-detail">{{ hasDetail(a) ? detailText(a) : '—' }}</span>
          </span>
        </li>
      </ul>
    </section>

    <!-- 过滤条:契约真有的两个参数(画板没画控件,这一条是补件 —— 见文件头第 ④ 条) -->
    <PanelCard :tone="TONE" padding="none">
      <div class="filters">
        <n-select
          v-model:value="eventTypeFilter"
          :options="eventTypeOptions"
          clearable
          placeholder="全部事件类型"
          class="sel"
          @update:value="applyFilters"
        />
        <n-input
          v-model:value="orderNoFilter"
          clearable
          placeholder="按单号筛选"
          class="search"
          @change="applyFilters"
        />
        <span class="grow" />
        <span class="count">共 {{ total }} 条</span>
      </div>
    </PanelCard>

    <PanelCard :tone="TONE" padding="md">
      <DataTable
        :tone="TONE"
        :columns="columns"
        :data="rows"
        :row-key="rowKeyOf"
        :loading="loading"
        :pagination="{ page, pageSize: PAGE_SIZE, itemCount: total, onPage: goPage }"
        empty-text="没有符合条件的审计事件 —— 换一个事件类型或单号再看"
      />
    </PanelCard>
  </div>
</template>

<style scoped>
/* 页头与各块之间的纵向节奏:画板是 16 —— 这里由 .page 的 gap 一处给 */
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 页头右端那句口径说明(画板:12px 三级灰,排在刷新键左边) */
.header-note {
  font-size: 12px;
  color: var(--sd-text3);
}

/* 刷新:画板的幽灵键(无底、1px 描边 border-strong、内边距 [8,14]、圆角 sm) */
.ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--sd-border-strong);
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.ghost:hover:not(:disabled) {
  color: var(--sd-text1);
  border-color: var(--sd-brand);
}

.ghost:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ── 告警流 ─────────────────────────────────────────── */

.alerts {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--sd-warning-border);
  border-radius: var(--sd-radius-lg);
  background: var(--sd-warning-bg);
}

.alerts-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.alerts-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

/* 标题与条数取语义 warning 的文字色(画板那颗琥珀字 $gov-tag-attention 同族) */
.alerts-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--sd-warning);
}

.alerts-count {
  flex: none;
  font-size: 12px;
  color: var(--sd-warning);
  font-variant-numeric: tabular-nums;
}

.alerts-error {
  margin: 0;
  font-size: 12px;
  line-height: 1.7;
  color: var(--sd-danger);
}

.alerts-loading {
  margin: 0;
  font-size: 12px;
  color: var(--sd-text3);
}

.alert-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* 一行一条告警:底取面板色(画板那一行的底就是 $ops-dark-panel) */
.alert-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: var(--sd-radius-md);
  background: var(--sd-surface);
}

/* 8×8 的圆点:严重度分色走语义色,不写画板取样出来的裸值 */
.alert-dot {
  width: 8px;
  height: 8px;
  flex: none;
  border-radius: var(--sd-radius-pill);
}

.dot-danger {
  background: var(--sd-danger);
}

.dot-warning {
  background: var(--sd-warning);
}

.alert-time {
  flex: none;
  font-size: 12px;
  color: var(--sd-text3);
  font-variant-numeric: tabular-nums;
}

.alert-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.alert-event {
  font-family: var(--sd-font-mono);
  font-size: 14px;
  font-weight: 600;
  color: var(--sd-text1);
}

/* 告警行里的「说明」是 detail 原文 —— 原样、可能很长,断行不截断 */
.alert-detail {
  font-family: var(--sd-font-mono);
  font-size: 12px;
  color: var(--sd-text3);
  font-variant-numeric: tabular-nums;
  word-break: break-all;
}

/* ── 过滤条 ─────────────────────────────────────────── */

.filters {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.sel {
  width: 240px;
}

.search {
  width: 220px;
}

.grow {
  flex: 1;
}

.count {
  font-size: 12px;
  color: var(--sd-text3);
  font-variant-numeric: tabular-nums;
}

/* ── 表格单元格(DataTable 渲染出来的,拿不到本组件的 scope 属性 —— 用 :deep 穿进去)── */

:deep(.mono) {
  font-family: var(--sd-font-mono);
  font-size: 12.5px;
  color: var(--sd-text1);
}

:deep(.dash) {
  color: var(--sd-text3);
}

/* 详情格:一行 JSON 预览 + 「明细」开关;展开的全文占满整格(照 O1 审计卡) */
:deep(.detail-cell) {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

:deep(.detail-json) {
  flex: 1;
  min-width: 0;
  /* 这个上限不是排版口味,是**布局正确性**的一部分:这一格是一行 nowrap 的 JSON,
     表格走 table-layout: auto,浏览器拿整段文字算这一列的最小宽度 ——
     1920 视口实测整张表被最长的一条明细撑到 3201px(容器只有 1238),
     五列声明的宽度形同虚设,末列的「明细」开关被推到面板外看不见。
     封一个上限(约 420px 的预览量)之后列宽回到正轨、省略号才真的生效;
     放开去看完整内容仍然走「明细」那个开关(展开的 pre 是换行的,不再撑表)。 */
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--sd-font-mono);
  font-size: 12px;
  color: var(--sd-text2);
  font-variant-numeric: tabular-nums;
}

:deep(.detail-toggle) {
  flex: none;
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: var(--sd-brand);
  cursor: pointer;
}

:deep(.detail-pre) {
  flex-basis: 100%;
  margin: 0;
  font-family: var(--sd-font-mono);
  font-size: 12px;
  line-height: 1.6;
  color: var(--sd-text2);
  white-space: pre-wrap;
  word-break: break-all;
}

@media (max-width: 1100px) {
  .filters {
    flex-wrap: wrap;
  }

  .sel,
  .search {
    width: 100%;
  }
}
</style>
